/**
 * Landscape PDF export — "Marathon Ventures Syndication Proposal Report".
 *
 * WHY THIS EXISTS
 * ---------------
 * The xlsx export is portrait, so printing or Save-as-PDF from Excel drops
 * every column right of "(000) HH" — CPM, Rate and Total Cost fall off the
 * page. Users were sending clients a 15-page document with no dollars on it.
 *
 * This reproduces the legacy Salesforce proposal report instead: landscape,
 * with the money columns intact.
 *
 *   Page 1 — quarter summary. One line per quarter + a plan total.
 *   Page 2 — program detail across all quarters.
 *
 * The legacy report also emits one page per quarter (secondary-demo
 * breakouts). Those are the same numbers sliced differently, so they're
 * deliberately out of scope here; add them later if anyone asks.
 *
 * DELIBERATELY DATA-ONLY
 * ----------------------
 * This module takes plain rows and knows nothing about Deal, estimates or
 * the component's state. MediaPlanBuilder owns `calcEstimateFields` and
 * exports it, so importing anything from there would create a cycle
 * (MediaPlanBuilder → pdf-export → MediaPlanBuilder). Keeping the boundary
 * at "already-computed numbers" avoids that and makes the layout testable
 * on fixture data.
 */

import { jsPDF } from 'jspdf';
// DEFAULT import. jspdf-autotable v5 exposes autoTable both ways, but the
// default is the one that resolves consistently:
//
//   dist/index.d.ts          export { autoTable as default };
//   *.mjs                    export { …, autoTable, autoTable as default }
//
// The named form type-checks under a plain `tsc -p tsconfig.json` but fails
// in CRA's fork-ts-checker with TS2693 ("only refers to a type"), because the
// two resolve the package differently — CRA rewrites tsconfig on start and
// the `exports` map is honoured inconsistently between them. The default
// import works under both.
//
// We're on v5 + jspdf v4 because jspdf 2.5.2 carries two critical CVEs:
// GHSA-wfv2-pwc8-crg5 (HTML injection, fixed 4.2.1) and GHSA-f8cm-6447-x5h2
// (path traversal, fixed 4.0.0). autotable v5 peers on "^2 || ^3 || ^4".
//
// `doc.lastAutoTable` still exists at runtime in v5 (the plugin assigns it
// after each table), which the quarter-page box positioning relies on.
import autoTable from 'jspdf-autotable';

/** Two-column identity block repeated at the top of every page. */
export interface ProposalPdfHeader {
  advertiser: string;
  agency: string;
  brand: string;
  buyer: string;
  marketplace: string;
  /** Broadcast vs calendar. MPB has no field for this; caller supplies. */
  calendarType: string;
  dealName: string;
  /** Working / Order / Planning Cost. Derived from plan status. */
  dealStatus: string;
  /** MPB has no AE field — caller passes the current user. */
  accountExec: string;
  primaryDemo: string;
  /** Appends "(Guaranteed)" after the demo, matching the legacy report. */
  guaranteed: boolean;
  equivalized: string;
  dealYear: string;
  /** Shown on the detail page only, mirroring the legacy layout. */
  estimatesAsOf?: string | null;
}

export interface QuarterSummaryRow {
  /** Display form, e.g. "4Q 2026". Blank marks a total row. */
  quarter: string;
  units: number;
  equivUnits: number;
  cpmHh: number | null;
  totalHh000: number | null;
  cpmDemo: number | null;
  totalDemo000: number | null;
  totalCost: number;
  /** Program subtotal — bold, but NOT inverted. */
  isTotal?: boolean;
  /** The single MVMS grand total — the only row with a black band. */
  isGrandTotal?: boolean;
  /** Blank separator row. Renders with no text and no rules. */
  isSpacer?: boolean;
}

/**
 * One page per quarter — the legacy report's pages 3..n.
 *
 * Same columns as the all-quarters detail, plus Unit Type and Unit Len,
 * and followed by a bordered summary box. `rows` is pre-ordered by the
 * caller: program rows, program subtotal, the "<net> <Q> Quarter Totals"
 * band, a spacer, then the "MVMS <Q> Quarter Totals" band.
 */
export interface QuarterPage {
  /** Quarter label used in the total row captions, e.g. "4Q26". */
  label: string;
  rows: QuarterPageRow[];
  box: QuarterBox;
}

export interface QuarterPageRow extends ProgramDetailRow {
  /** ":30" style label, blank on total rows. */
  unitType?: string;
  unitLen?: string;
  /**
   * One entry per secondary demo, in the same order as the
   * `secondaryDemos` array passed to downloadProposalPdf. Each renders as
   * a (000) + CPM column pair under its own black sub-band.
   */
  secondary?: Array<{ total000: number | null; cpm: number | null }>;
  isGrandTotal?: boolean;
  isSpacer?: boolean;
}

/**
 * The bordered block at the bottom of each quarter page. Mirrors the
 * Excel summary the sales team reads off — dollars, a unit-length
 * breakdown, then impressions/CPM and the universe figures.
 */
export interface QuarterBox {
  totalDollars: number;
  /** Keyed by unit length ("15", "30", …) → equivalized unit count. */
  byLen: Record<string, number>;
  totalUnits: number;
  totalEquivUnits: number;
  hh000: number;
  hhCpm: number | null;
  demo000: number;
  demoCpm: number | null;
  universeType: string;
  hhUniverse: number | null;
  demoUniverse: number | null;
}

export interface ProgramDetailRow {
  program: string;
  period: string;
  dayTime: string;
  units: number;
  equivUnits: number;
  rtgHh: number | null;
  hh000: number | null;
  cpmHh: number | null;
  totalHh000: number | null;
  rtgDemo: number | null;
  vpvh: number | null;
  demo000: number | null;
  totalDemo000: number | null;
  cpmDemo: number | null;
  /**
   * Cost per equivalized unit. NOT rendered — the column was dropped at
   * the client's request. Kept on the type because the caller still
   * computes it and it's the obvious thing to re-add if they change
   * their mind; costs nothing to carry.
   */
  avgUnitRate?: number | null;
  totalCost: number;
  /** Trailing equivalized CPM column in the legacy layout. */
  equivCpmDemo?: number | null;
  isTotal?: boolean;
}

/* ── Formatting helpers ──────────────────────────────────────────────────
   Kept local so the PDF's number formatting can't drift when the on-screen
   formatters change. A client-facing document should be stable. */

const n0 = (v: number | null | undefined): string =>
  v == null ? '' : Math.round(v).toLocaleString('en-US');

const n1 = (v: number | null | undefined): string =>
  v == null ? '' : v.toFixed(1);

const n2 = (v: number | null | undefined): string =>
  v == null ? '' : v.toFixed(2);

const n3 = (v: number | null | undefined): string =>
  v == null ? '' : v.toFixed(3);

const usd0 = (v: number | null | undefined): string =>
  v == null ? '' : '$' + Math.round(v).toLocaleString('en-US');

const usd2 = (v: number | null | undefined): string =>
  v == null ? '' : '$' + v.toFixed(2);

/* ── Page geometry (points; 72pt = 1in) ─────────────────────────────── */
const MARGIN = 28;
const HEADER_TOP = 30;
const HEADER_LINE_H = 11;
const TITLE = 'Marathon Ventures Syndication Proposal Report';

/**
 * Draw the repeating identity block. Returns the Y coordinate the table
 * should start at, so callers never hardcode a table offset that silently
 * overlaps when a header line is added.
 */
function drawHeader(
  doc: jsPDF,
  h: ProposalPdfHeader,
  opts: { showEstimatesAsOf?: boolean } = {},
): number {
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(TITLE, pageW / 2, HEADER_TOP, { align: 'center' });

  const left: Array<[string, string]> = [
    ['Advertiser:', h.advertiser],
    ['Agency:', h.agency],
    ['Brand:', h.brand],
    ['Buyer:', h.buyer],
    ['Marketplace:', h.marketplace],
    ['Calendar Type:', h.calendarType],
  ];
  const right: Array<[string, string]> = [
    ['Deal Name:', h.dealName],
    ['Deal Status:', h.dealStatus],
    ['Account Exec:', h.accountExec],
    ['Primary Demo:', h.primaryDemo + (h.guaranteed ? ' (Guaranteed)' : '')],
    ['Equivalized:', h.equivalized],
    ['Deal Year:', h.dealYear],
  ];

  doc.setFontSize(8);
  const y0 = HEADER_TOP + 16;
  const rightX = pageW / 2 + 10;

  const drawCol = (rows: Array<[string, string]>, x: number) => {
    rows.forEach(([label, value], i) => {
      const y = y0 + i * HEADER_LINE_H;
      doc.setFont('helvetica', 'bold');
      doc.text(label, x, y);
      doc.setFont('helvetica', 'normal');
      doc.text(String(value ?? ''), x + 62, y);
    });
  };
  drawCol(left, MARGIN);
  drawCol(right, rightX);

  const bottom = y0 + left.length * HEADER_LINE_H;

  // "Estimates as of" sits above the table on the detail page only —
  // matches the legacy report, and it matters because two plans built on
  // different estimate versions are not comparable.
  if (opts.showEstimatesAsOf && h.estimatesAsOf) {
    doc.setFont('helvetica', 'bold');
    doc.text('Estimates as of:', pageW - MARGIN - 110, y0 - HEADER_LINE_H);
    doc.setFont('helvetica', 'normal');
    doc.text(String(h.estimatesAsOf), pageW - MARGIN - 40, y0 - HEADER_LINE_H);
  }

  return bottom + 6;
}

/** Shared autoTable styling so both pages look like one document. */
function tableTheme(startY: number) {
  return {
    startY,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'plain' as const,
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
      overflow: 'linebreak' as const,
    },
    headStyles: {
      fontStyle: 'bold' as const,
      fontSize: 6.5,
      valign: 'bottom' as const,
      lineWidth: { bottom: 0.75 },
      lineColor: [0, 0, 0] as [number, number, number],
      // NO halign here. In autotable v3 `columnStyles` is applied to the
      // BODY section only — it never reaches `head`. So a header's
      // alignment can only come from headStyles (all columns, no good) or
      // from per-cell styles set in didParseCell. We use the latter; see
      // alignHeadCells below. Setting anything here would apply to the
      // text columns too and fight it.
    },
  };
}

/** Black band with white text — group headers and total rows. */
const INVERTED = {
  fillColor: [0, 0, 0] as [number, number, number],
  textColor: [255, 255, 255] as [number, number, number],
};

/**
 * Right-align the HEAD cells of numeric columns.
 *
 * Necessary because autotable v3 resolves `columnStyles` for the body
 * section only — head cells never see it. Without this the column labels
 * render at their default alignment while the values sit hard right, which
 * is the ~12pt drift visible when you measure the two.
 *
 * `headRowIndex` targets the label row specifically: on the detail table
 * row 0 is the black group band, which carries its own centred alignment
 * and must not be overwritten.
 */
function alignHeadCells(data: any, textCols: number, headRowIndex: number): void {
  if (data.section !== 'head') return;
  if (data.row.index !== headRowIndex) return;
  if (data.column.index >= textCols) data.cell.styles.halign = 'right';
}

/**
 * Right-align every column except the leading text ones.
 * `textCols` is the count of left-aligned columns at the start.
 */
function alignCols(total: number, textCols: number, widths: Record<number, number> = {}) {
  const out: Record<number, any> = {};
  for (let i = 0; i < total; i++) {
    out[i] = {
      halign: i < textCols ? ('left' as const) : ('right' as const),
      ...(widths[i] != null ? { cellWidth: widths[i] } : {}),
    };
  }
  return out;
}

/**
 * Build and download the proposal PDF.
 *
 * `demoLabel` is the primary demo name used in column headers ("A25-54"),
 * so the columns read the same way the legacy report does rather than a
 * generic "Demo".
 */
export function downloadProposalPdf(params: {
  filename: string;
  header: ProposalPdfHeader;
  demoLabel: string;
  quarterRows: QuarterSummaryRow[];
  detailRows: ProgramDetailRow[];
  /** One entry per quarter with data. Empty array = pages 1–2 only. */
  quarterPages?: QuarterPage[];
  /** Secondary demo names, in display order. Drives the extra columns. */
  secondaryDemos?: string[];
}): void {
  const { filename, header, demoLabel, quarterRows, detailRows } = params;
  const quarterPages = params.quarterPages ?? [];
  const secondaryDemos = params.secondaryDemos ?? [];

  // Letter landscape, points. Letter (not A4) because these go to US
  // clients and print on US paper.
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

  /* ── PAGE 1 — quarter summary ─────────────────────────────────────── */
  const startY1 = drawHeader(doc, header);

  autoTable(doc, {
    ...tableTheme(startY1),
    head: [[
      'Quarter', 'Unit\n#', 'Equiv.\nUnits',
      'CPM\nHH', `Total\n(000)\nHH`,
      `CPM\n${demoLabel}`, `Total\n(000)\n${demoLabel}`,
      'Total\nCost',
    ]],
    body: quarterRows.map(r => (r.isSpacer
      ? ['', '', '', '', '', '', '', '']
      : [
        r.quarter,
        n1(r.units),
        n2(r.equivUnits),
        usd2(r.cpmHh),
        n0(r.totalHh000),
        usd2(r.cpmDemo),
        n0(r.totalDemo000),
        usd0(r.totalCost),
      ])),
    // Column 0 is RIGHT-aligned, not left. Quarter labels ("4Q 2026") then
    // line up on their right edge directly under the "Quarter" header,
    // while the longer total labels ("Front Office Sports", "MVMS
    // 4Q26-3Q27 Totals") extend leftward past them. That's what makes the
    // total rows visually stand out in the legacy report — they hang out
    // to the left of the data block rather than sharing its left edge.
    columnStyles: alignCols(8, 0, { 0: 190 }),
    didParseCell: (data: any) => {
      // Single head row here, so index 0 is the label row.
      alignHeadCells(data, 1, 0);
      if (data.section !== 'body') return;
      const src = quarterRows[data.row.index];
      if (src?.isSpacer) {
        // Blank separator: no text, no rules, just vertical breathing
        // room before the grand total.
        data.cell.styles.lineWidth = 0;
        data.cell.styles.minCellHeight = 8;
        return;
      }
      // Program subtotals are bold only. The MVMS grand total is the one
      // row that gets the black band.
      if (src?.isGrandTotal) {
        data.cell.styles.fontStyle = 'bold';
        Object.assign(data.cell.styles, INVERTED);
      } else if (src?.isTotal) {
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  /* ── PAGE 2 — program detail, all quarters ────────────────────────── */
  doc.addPage();
  const startY2 = drawHeader(doc, header, { showEstimatesAsOf: true });

  autoTable(doc, {
    ...tableTheme(startY2 + 12),
    // Two header rows: a group band ("Household" / "Primary Demo") over
    // the column labels, matching the legacy report. colSpan cells with
    // empty content carry the spacing for the ungrouped leading columns.
    head: [
      // Group band. Black fill + white text, per the legacy report.
      // The leading 5 and trailing 2 columns are ungrouped, so those
      // spans carry no fill and no rule.
      [
        { content: '', colSpan: 5, styles: { lineWidth: 0 } },
        { content: 'Household', colSpan: 4,
          styles: { halign: 'center' as const, ...INVERTED } },
        { content: 'Primary Demo', colSpan: 6,
          styles: { halign: 'center' as const, ...INVERTED } },
        { content: '', colSpan: 1, styles: { lineWidth: 0 } },
      ],
      [
        'Program', 'Period', 'Day/Time', 'Unit\n#', 'Equiv.\nUnits',
        'Avg Rtg\nHH', 'Avg (000)\nHH', 'CPM\nHH', `Total (000)\nHH`,
        `Avg Rtg\n${demoLabel}`, `VPVH\n${demoLabel}`, `Avg (000)\n${demoLabel}`,
        `Total (000)\n${demoLabel}`, `Avg CPM\n${demoLabel}`, `CPM\n${demoLabel}`,
        'Total\nCost',
      ],
    ],
    body: detailRows.map(r => ([
      r.program,
      r.period,
      r.dayTime,
      n1(r.units),
      n2(r.equivUnits),
      n3(r.rtgHh),
      n0(r.hh000),
      usd2(r.cpmHh),
      n0(r.totalHh000),
      n3(r.rtgDemo),
      n3(r.vpvh),
      n0(r.demo000),
      n0(r.totalDemo000),
      usd2(r.cpmDemo),
      usd2(r.equivCpmDemo),
      usd0(r.totalCost),
    ])),
    // Explicit widths on the three text columns; the 13 numeric columns
    // share what's left. Without a cap the Program column absorbed the
    // slack and rendered enormous next to hairline number columns.
    columnStyles: alignCols(16, 3, { 0: 128, 1: 32, 2: 42 }),
    didParseCell: (data: any) => {
      // Head row 1 is the label row; row 0 is the black group band and
      // keeps its own centred alignment.
      alignHeadCells(data, 3, 1);
      if (data.section === 'body') {
        const src = detailRows[data.row.index];
        if (src?.isTotal) {
          data.cell.styles.fontStyle = 'bold';
          Object.assign(data.cell.styles, INVERTED);
        }
      }
    },
    // Repeat the identity block on every overflow page — a 40-program plan
    // spills past one page and the continuation must still be identifiable
    // if someone prints and separates the sheets.
    didDrawPage: (data: any) => {
      if (data.pageNumber > 1) drawHeader(doc, header, { showEstimatesAsOf: true });
    },
  });

  /* ── PAGES 3..n — one per quarter, each with a totals box ─────────── */
  // Unit lengths listed in the box. Fixed order, and always all six, even
  // at 0.0 — the legacy report prints the full ladder so a reader can see
  // at a glance that nothing is hiding in a length they didn't scan for.
  const BOX_LENGTHS = ['15', '30', '45', '60', '90', '120'];

  for (const qp of quarterPages) {
    doc.addPage();
    const startY = drawHeader(doc, header, { showEstimatesAsOf: true });

    // ── Column plan ────────────────────────────────────────────────
    // Narrow blank GAP columns separate the black band groups, matching
    // the reference where Household / Primary Demo / Secondary Demos are
    // three detached rectangles rather than one continuous strip.
    const nSec = secondaryDemos.length;
    const hasSec = nSec > 0;
    const GAP = 6;                       // width of a separator column
    // Text + unit columns before HH: Program, Period, Day/Time, Unit Len,
    // Unit #, Equiv. Units.
    //
    // Was 7 — it included a "Unit Type" column that rendered blank in every
    // export ever produced, because `unitType` is hardcoded to '' at all three
    // call sites in MediaPlanBuilder. It cost 24pt of a page that has none to
    // spare once secondary demos are added.
    //
    // The plumbing is deliberately left in place (the `unitType` field on
    // QuarterPageRow, and the callers that set it) so restoring the column is
    // just re-adding its label, its body cell and its width — nothing has to
    // be re-derived.
    const LEAD = 6;
    const iGap1 = LEAD;                  // gap before Household
    const iHH = iGap1 + 1;               // 4 HH columns
    const iGap2 = iHH + 4;               // gap before Primary Demo
    const iDemo = iGap2 + 1;             // 5 primary-demo columns
    const iGap3 = iDemo + 5;             // gap before Secondary Demos
    // With no secondary demos there is no third gap column either — the
    // head/body rows don't emit one, so reserving an index for it would
    // make nCols one wider than the real table and the blank spacer rows
    // (built as `new Array(nCols)`) would render an extra trailing cell.
    const iSec = hasSec ? iGap3 + 1 : iGap3;
    const iTail = iSec + nSec * 2;       // Rate, Total Cost
    const nCols = iTail + 2;

    const blankCell = { content: '', styles: { lineWidth: 0 } };
    const bandCell = (label: string, span: number) => ({
      content: label, colSpan: span,
      styles: { halign: 'center' as const, ...INVERTED },
    });

    // Row 0 carries ONLY the "Secondary Demos" umbrella — in the
    // reference it sits one line higher than the other bands, spanning
    // the per-demo sub-bands beneath it.
    const bandRow0: any[] = [
      { content: '', colSpan: iSec, styles: { lineWidth: 0 } },
      ...(nSec > 0 ? [bandCell('Secondary Demos', nSec * 2)] : []),
      { content: '', colSpan: 2, styles: { lineWidth: 0 } },
    ];
    // Row 1: Household / Primary Demo, plus one sub-band per secondary
    // demo, separated by the blank gap columns.
    const bandRow1: any[] = [
      { content: '', colSpan: LEAD, styles: { lineWidth: 0 } },
      blankCell,
      bandCell('Household', 4),
      blankCell,
      bandCell('Primary Demo', 5),
      ...(nSec > 0 ? [blankCell] : []),
      ...secondaryDemos.map(d => bandCell(d, 2)),
      { content: '', colSpan: 2, styles: { lineWidth: 0 } },
    ];

    const labelRow: any[] = [
      'Network/Program', 'Period', 'Day/Time', 'Unit\nLen',
      'Unit\n#', 'Equiv.\nUnits',
      '',
      // "Total (000)" is broken across two lines deliberately. As one line it
      // renders 31.4pt at the 6.5pt head size — far wider than any neighbour
      // ("W18-49" 22.8, "A12-24" 21.0, "CPM" 14.4) — so on a crowded quarter
      // page autoTable wrapped it at an arbitrary point ("Tota / l (00 / 0)").
      // Choosing the break point ourselves costs no page width and puts the
      // widest line at 15.2pt, below the demo labels.
      'Rtg\nHH', '(000)\nHH', 'CPM\nHH', 'Total\n(000)\nHH',
      '',
      `Rtg\n${demoLabel}`, `VPVH\n${demoLabel}`, `(000)\n${demoLabel}`,
      `Total\n(000)\n${demoLabel}`, `CPM\n${demoLabel}`,
      ...(nSec > 0 ? [''] : []),
      ...secondaryDemos.flatMap(d => [`(000)\n${d}`, `CPM\n${d}`]),
      'Rate', 'Total\nCost',
    ];

    // Fixed widths for the leading label columns. Everything else is
    // auto-width, so every point trimmed here is redistributed across the ~23
    // numeric columns — which is what stops values wrapping mid-number
    // ("$45,70 / 7") once several secondary demos are in play.
    //
    // Measured from a 5-secondary-demo export rather than guessed. At the
    // 5.6pt body font the widest content in each column is:
    //
    // ⚠ A column must be at least (widest rendered string + 6). The theme sets
    // cellPadding left:3 right:3, so 6pt of every allocation is unusable. An
    // earlier pass sized these from measured text extents and forgot that,
    // landing Period and Day/Time ~2-3pt short — which doesn't truncate, it
    // WRAPS the header ("Perio / d", "Day/Tim / e").
    //
    // Widths measured from rendered PDFs. Head renders at 6.5pt, body at 5.6pt,
    // so a header is usually the widest thing in a label column:
    //
    //   col 0  Program   "MVMS Q426 Quarter Totals"   71.9  ← the cap; NOT a
    //                    "Front Office Sports Tonight" ~71    program name
    //                    "Network/Program" (header)    54.2
    //   col 1  Period    "Period" (header)             20.2  ← header governs
    //                    "4Q'26"                       14.8
    //   col 2  Day/Time  "Day/Time" (header)           28.9  ← header governs
    //                    "Wknd"                        14.3
    //   col 3  Unit Len  "Unit" (header, line 1)       12.6
    //
    // Allocations below are widest + 6 + ~2pt buffer. Everything else on the
    // row is auto-width, so whatever is trimmed here is redistributed across
    // the ~23 numeric columns — which is what stops VALUES wrapping mid-number
    // ("$45,70 / 7") once several secondary demos are in play.
    //
    // Floors, if anyone tunes these again: 78 / 27 / 35 / 19.
    const gapWidths: Record<number, number> = { 0: 90, 1: 28, 2: 37, 3: 22 };
    gapWidths[iGap1] = GAP;
    gapWidths[iGap2] = GAP;
    if (nSec > 0) gapWidths[iGap3] = GAP;

    autoTable(doc, {
      ...tableTheme(startY + 20),
      styles: { ...tableTheme(0).styles, fontSize: nSec > 1 ? 5.6 : 6.4 },
      // Head must shrink WITH the body on this table. The shared theme renders
      // heads at 6.5pt, but 15 of these columns are sized by a demo label
      // ("W18-49", "A12-34") rather than by their values — so at 5 secondary
      // demos the head font, not the data, is what decides whether the row
      // fits. Leaving it at 6.5 while the body dropped to 5.6 cost ~45pt and
      // wrapped nearly every header mid-word ("W18-4 / 9", "Rat / e").
      headStyles: { ...tableTheme(0).headStyles, fontSize: nSec > 1 ? 5.6 : 6.4 },
      head: [bandRow0, bandRow1, labelRow],
      body: qp.rows.map(r => (r.isSpacer
        ? new Array(nCols).fill('')
        : [
          r.program, r.period, r.dayTime, r.unitLen ?? '',
          n1(r.units), n2(r.equivUnits),
          '',
          n3(r.rtgHh), n0(r.hh000), usd2(r.cpmHh), n0(r.totalHh000),
          '',
          n3(r.rtgDemo), n3(r.vpvh), n0(r.demo000), n0(r.totalDemo000),
          usd2(r.cpmDemo),
          ...(nSec > 0 ? [''] : []),
          ...secondaryDemos.flatMap((_, i) => {
            const s = r.secondary?.[i];
            return [n0(s?.total000 ?? null), usd2(s?.cpm ?? null)];
          }),
          usd0(r.avgUnitRate), usd0(r.totalCost),
        ])),
      columnStyles: alignCols(nCols, 3, gapWidths),
      didParseCell: (data: any) => {
        // Label row is index 2 now (two band rows above it).
        alignHeadCells(data, 3, 2);
        // Gap columns stay blank and border-free in EVERY section, so the
        // black bands read as separate rectangles.
        const isGap = data.column.index === iGap1
                   || data.column.index === iGap2
                   || (nSec > 0 && data.column.index === iGap3);
        if (isGap) {
          data.cell.styles.lineWidth = 0;
          data.cell.styles.fillColor = false;
          if (data.section !== 'head') return;
        }
        if (data.section !== 'body') return;
        const src = qp.rows[data.row.index];
        if (src?.isSpacer) {
          data.cell.styles.lineWidth = 0;
          data.cell.styles.minCellHeight = 8;
          return;
        }
        if (src?.isGrandTotal) {
          data.cell.styles.fontStyle = 'bold';
          // Gap columns must NOT be inverted — the black total band is
          // broken by the same separators as the header bands.
          if (!isGap) Object.assign(data.cell.styles, INVERTED);
        } else if (src?.isTotal) {
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    /* ── Summary box, bottom-left under the table ─────────────────── */
    const afterTableY = (doc as any).lastAutoTable?.finalY ?? startY + 120;
    const boxRows: Array<[string, string, boolean]> = [
      ['Total Dollars', usd0(qp.box.totalDollars), true],
      ...BOX_LENGTHS.map(len => (
        [`      Total :${len}`, n1(qp.box.byLen[len] ?? 0), false] as [string, string, boolean]
      )),
      ['Total Units', n1(qp.box.totalUnits), true],
      ['Total Equiv Units', n2(qp.box.totalEquivUnits), true],
      ['HH (000)', n0(qp.box.hh000), true],
      ['HH CPM', usd2(qp.box.hhCpm), true],
      [`${demoLabel} (000)`, n0(qp.box.demo000), true],
      [`${demoLabel} CPM`, usd2(qp.box.demoCpm), true],
      ['Universe Type', qp.box.universeType, true],
      ['HH Universe', n0(qp.box.hhUniverse), true],
      [`${demoLabel} Universe`, n0(qp.box.demoUniverse), true],
    ];

    const BOX_W = 250;
    const boxStartY = afterTableY + 14;
    autoTable(doc, {
      startY: boxStartY,
      margin: { left: MARGIN },
      theme: 'plain',
      tableWidth: BOX_W,
      styles: {
        font: 'helvetica', fontSize: 7,
        cellPadding: { top: 1.5, right: 4, bottom: 1.5, left: 4 },
        lineWidth: 0,
      },
      body: boxRows.map(([label, value]) => [label, value]),
      columnStyles: {
        0: { halign: 'left', cellWidth: 160 },
        1: { halign: 'right', cellWidth: 90 },
      },
      didParseCell: (data: any) => {
        const [, , bold] = boxRows[data.row.index];
        if (bold) data.cell.styles.fontStyle = 'bold';
      },
    });

    // Single rectangle around the whole block rather than per-cell
    // borders — matches the legacy report, which draws one box.
    // Drawn AFTER the table: autotable v3 has no `didDrawTable` hook
    // (only didDrawPage / didDrawCell), and `lastAutoTable.finalY` gives
    // the exact bottom edge once the rows are laid out.
    const boxEndY = (doc as any).lastAutoTable?.finalY ?? boxStartY;
    doc.setDrawColor(0);
    doc.setLineWidth(0.75);
    doc.rect(MARGIN, boxStartY, BOX_W, boxEndY - boxStartY);
  }

  /* ── Footer: page numbers on every page ───────────────────────────── */
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() - MARGIN,
      doc.internal.pageSize.getHeight() - 14,
      { align: 'right' },
    );
  }

  doc.save(filename);
}
